import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Input } from './ui/input';
import { Check, ChevronsUpDown, Search, X } from 'lucide-react';

/**
 * SearchableSelect — a drop-in replacement for the shadcn <Select> when the list
 * is long (30+ items). Guarantees every option is discoverable regardless of
 * position by adding a live type-to-filter input at the top of the dropdown.
 *
 * Also defensively de-duplicates items by `value` and skips items with an
 * empty/falsy `value` (which silently break Radix Select).
 *
 * Props:
 *   value    – currently selected value (string)
 *   onChange – (nextValue) => void
 *   options  – Array<{ value: string, label: string, hint?: string }>
 *   placeholder – shown when nothing is selected
 *   testId   – base data-testid, appended with option value / trigger suffix
 *   disabled – bool
 */
const SearchableSelect = ({ value, onChange, options = [], placeholder = 'Select…', testId, disabled }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  // Clean list: skip items without a truthy value, dedupe by value, keep first occurrence
  const cleanOptions = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const o of options) {
      if (!o || !o.value) continue;
      if (seen.has(o.value)) continue;
      seen.add(o.value);
      out.push(o);
    }
    return out;
  }, [options]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return cleanOptions;
    return cleanOptions.filter((o) =>
      (o.label || '').toLowerCase().includes(s) || (o.hint || '').toLowerCase().includes(s)
    );
  }, [cleanOptions, q]);

  const selected = cleanOptions.find((o) => o.value === value);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  // Focus the search field when dropdown opens
  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 30);
  }, [open]);

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        className={`flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background hover:bg-accent/40 focus:outline-none focus:ring-2 focus:ring-emerald-600 ${disabled ? 'cursor-not-allowed opacity-50' : ''}`}
        data-testid={testId ? `${testId}-trigger` : undefined}
      >
        <span className={`truncate text-left ${selected ? 'text-slate-900' : 'text-slate-500'}`}>
          {selected ? selected.label : placeholder}
        </span>
        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
      </button>

      {open && (
        <div
          className="absolute z-50 mt-1 w-full min-w-[220px] rounded-md border border-slate-200 bg-white shadow-lg overflow-hidden"
          data-testid={testId ? `${testId}-menu` : undefined}
        >
          <div className="flex items-center gap-2 border-b border-slate-100 bg-slate-50 px-2 py-1.5">
            <Search className="h-3.5 w-3.5 text-slate-400" />
            <Input
              ref={inputRef}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={`Search ${cleanOptions.length} items…`}
              className="h-7 border-0 bg-transparent p-0 text-sm focus-visible:ring-0 shadow-none"
              data-testid={testId ? `${testId}-search` : undefined}
            />
            {q && (
              <button type="button" onClick={() => setQ('')} className="text-slate-400 hover:text-slate-600">
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
          <div className="max-h-64 overflow-y-auto py-1">
            {filtered.length === 0 ? (
              <div className="px-3 py-4 text-center text-sm text-slate-400 italic">
                No matches for &ldquo;{q}&rdquo;
              </div>
            ) : (
              filtered.map((o) => {
                const active = o.value === value;
                return (
                  <button
                    key={o.value}
                    type="button"
                    onClick={() => { onChange(o.value); setOpen(false); setQ(''); }}
                    className={`flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left text-sm hover:bg-emerald-50 ${active ? 'bg-emerald-50 font-medium text-emerald-900' : 'text-slate-700'}`}
                    data-testid={testId ? `${testId}-option-${o.value}` : undefined}
                  >
                    <span className="truncate">{o.label}</span>
                    {active && <Check className="h-4 w-4 text-emerald-700 shrink-0" />}
                  </button>
                );
              })
            )}
          </div>
          <div className="border-t border-slate-100 bg-slate-50 px-3 py-1 text-[10px] text-slate-500 font-mono">
            {filtered.length} / {cleanOptions.length} shown
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchableSelect;
